import { useOrgAuthStore } from "@solomon-apps/shared";
import { Heading } from "@solomon-apps/shared/src/components/ui/heading";
import { Text } from "@solomon-apps/shared/src/components/ui/text";
import { easings } from "@solomon-apps/shared/src/utils/easings";
import {
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    UploadFiles
} from "@solomon-labs/design/components/ui";
import { motion } from "motion/react";
import { type ReactElement, useMemo, useState } from "react";

import { CountrySelect } from "@/components/CountrySelect";
import { LegalEntityTypeSelect } from "@/components/LegalEntityTypeSelect";
import {
    type LegalEntityPayload,
    submitLegalEntity
} from "@/services/clientOnboarding";
import {
    type OnboardingDocumentPurpose,
    uploadOnboardingDocument
} from "@/services/clientOnboardingDocuments";
import { resolveClientFormError } from "@/utils/clientFormErrors";

import { useOnboardingApplicationContextOrNull } from "../OnboardingApplicationContext";
import { useReportOnboardingDraft } from "../onboardingDraftContext";
import {
    type LegalContactSource,
    type LegalEntityDocsState,
    useLegalEntityFormState,
    useSignatoryFormState
} from "../onboardingFormStateContext";
import { FormCard } from "../panel/FormCard";
import { FormPrimaryAction } from "../panel/FormPrimaryAction";
import { DOCUMENT_ACCEPT, DOCUMENT_MAX_SIZE } from "../panel/uploadConstraints";
import type { NextStepTarget } from "../types";

import {
    buildLegalEntityStepPatch,
    legalEntityMissingFieldLabels,
    legalEntityPatchIsDirty
} from "./legalEntityFormModel";

interface DocSlot {
    purpose: OnboardingDocumentPurpose;
    label: string;
    payloadKey: keyof Pick<
        LegalEntityPayload,
        | "formationDocumentUuid"
        | "operatingDocumentUuid"
        | "certificateOfGoodStandingUuid"
    >;
}

/**
 * `""` is the null state — a real, always-present row rather than a
 * placeholder, so the choice is reversible once a person has been picked.
 * It leads the list because it is the default.
 */
const LEGAL_CONTACT_OPTIONS: Array<{
    value: LegalContactSource | "";
    label: string;
}> = [
    { value: "", label: "Different Than Existing Contacts" },
    { value: "primary", label: "Same as Primary Contact" },
    { value: "signatory", label: "Same as Authorized Signatory" }
];

const DOC_SLOTS: DocSlot[] = [
    {
        purpose: "formation_document",
        label: "Formation Document",
        payloadKey: "formationDocumentUuid"
    },
    {
        purpose: "operating_document",
        label: "Operating Document",
        payloadKey: "operatingDocumentUuid"
    },
    {
        purpose: "certificate_of_good_standing",
        label: "Certificate of Good Standing",
        payloadKey: "certificateOfGoodStandingUuid"
    }
];

/**
 * Structured form for §B.1 Legal Entity. Three required uploads
 * (Formation, Operating, Certificate of Good Standing) plus the field
 * inputs the backend payload schema demands. Replaces the per-slot
 * "accepted docs" panel that the generic DetailPanel renders.
 */
export function LegalEntityForm({
    nextStep,
    onContinue,
    slotIdx
}: {
    /** When non-null and the form is in a saved-clean state, the
     *  bottom button flips from "Saved" to "Continue to {title} →"
     *  and clicking it advances the hub to that subsection. */
    nextStep?: NextStepTarget | null;
    onContinue?: () => void;
    /** Focused sub-row from the rail. When set, only that sub-row's blocks
     *  render, so a rail row and its panel agree. Undefined (the section row
     *  itself) renders everything. Indices track `slots` order in
     *  `spec/onboardingSpec.ts`. */
    slotIdx?: number;
} = {}): ReactElement {
    // Sub-row 0 is "Entity Details"; the three documents are sub-rows 1-3.
    // Every field belongs to exactly one sub-row — nothing renders only at
    // section level, so the rail is a complete map of the work.
    // Slot 0 is Entity Details, slot 1 Entity Contacts. Both render at
    // section level (slotIdx undefined); the rail focuses one at a time.
    const showsDetails = slotIdx === undefined || slotIdx === 0;
    const showsContacts = slotIdx === undefined || slotIdx === 1;
    const context = useOnboardingApplicationContextOrNull();
    const me = useOrgAuthStore((s) => s.me);
    const setMe = useOrgAuthStore((s) => s.setMe);

    // Field values + lastSubmitted snapshot live in the hub-scoped
    // form-state context so edits survive navigation. Server hydration
    // (and the org legalName / display-name seeds) is handled once inside
    // OnboardingFormStateProvider, so there's no per-mount hydration
    // effect here. This form reads/writes only the legal-entity slice of
    // the shared state; the tax fields on the same context object are
    // owned by TaxIdForm (kept as a separate section in the May-20 IA).
    const [state, setState] = useLegalEntityFormState();
    const [signatory] = useSignatoryFormState();
    const {
        legalName,
        tradingName,
        entityType,
        registeredAddress,
        companyNumber,
        jurisdiction,
        primaryContactName,
        primaryContactEmail,
        legalContactSource,
        legalContactName,
        legalContactEmail,
        docs,
        lastSubmitted
    } = state;

    const [uploadingPurpose, setUploadingPurpose] =
        useState<OnboardingDocumentPurpose | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleUpload = async (
        purpose: OnboardingDocumentPurpose,
        files: FileList | null
    ): Promise<void> => {
        if (!files || files.length === 0) return;
        const file = files[0];
        if (!context) {
            setState((prev) => ({
                ...prev,
                docs: {
                    ...prev.docs,
                    [purpose]: {
                        documentUuid: `mock-${Date.now()}`,
                        filename: file.name
                    }
                }
            }));
            return;
        }
        setUploadingPurpose(purpose);
        setError(null);
        try {
            const doc = await uploadOnboardingDocument({
                applicationUuid: context.application.uuid,
                purpose,
                file
            });
            setState((prev) => ({
                ...prev,
                docs: {
                    ...prev.docs,
                    [purpose]: {
                        documentUuid: doc.uuid,
                        filename: doc.filename
                    }
                }
            }));
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Document upload failed. Choose the file again and retry."
                )
            );
        } finally {
            setUploadingPurpose(null);
        }
    };

    const currentPatch = useMemo<LegalEntityPayload | null>(() => {
        return buildLegalEntityStepPatch({ state, signatory, slotIdx });
    }, [state, signatory, slotIdx]);
    const submittable = currentPatch !== null;
    /** Named under the disabled Save. Uploads are the usual answer:
     *  the fields can all be filled and the step still be incomplete. */
    const incompleteHint = useMemo<string | null>(() => {
        if (currentPatch !== null) return null;
        const missing = legalEntityMissingFieldLabels({
            state,
            signatory,
            slotIdx
        });
        if (missing.length === 0) return null;
        return `Still needed: ${missing.join(", ")}.`;
    }, [currentPatch, state, signatory, slotIdx]);

    const isDirty = legalEntityPatchIsDirty(currentPatch, lastSubmitted);
    const hasPartialInput =
        legalName.trim().length > 0 ||
        tradingName.trim().length > 0 ||
        entityType.selection !== null ||
        entityType.other.trim().length > 0 ||
        registeredAddress.trim().length > 0 ||
        companyNumber.trim().length > 0 ||
        jurisdiction !== null ||
        primaryContactName.trim().length > 0 ||
        primaryContactEmail.trim().length > 0 ||
        legalContactName.trim().length > 0 ||
        legalContactEmail.trim().length > 0 ||
        docs.formation_document !== null ||
        docs.operating_document !== null ||
        docs.certificate_of_good_standing !== null;
    const hasDraft = isDirty || (lastSubmitted === null && hasPartialInput);
    useReportOnboardingDraft("legal-entity", hasDraft);

    const handleSave = async (): Promise<void> => {
        if (!currentPatch) return;
        if (!context) {
            setState((prev) => ({
                ...prev,
                lastSubmitted: {
                    ...(prev.lastSubmitted ?? {}),
                    ...currentPatch
                }
            }));
            return;
        }
        setSubmitting(true);
        setError(null);
        try {
            const result = await submitLegalEntity(
                context.application.uuid,
                currentPatch
            );
            context.applyStepUpdate(result);
            const savedPayload = result.step.payload as LegalEntityPayload;
            setState((prev) => ({ ...prev, lastSubmitted: savedPayload }));
            // Mirror the BE projection onto the local Org snapshot so the
            // onboarding header + greeting flip to the canonical legal
            // name immediately, without waiting for a /me refetch.
            if (
                me &&
                currentPatch.legalName &&
                me.org.legalName !== currentPatch.legalName
            ) {
                setMe({
                    ...me,
                    org: { ...me.org, legalName: currentPatch.legalName }
                });
            }
        } catch (err) {
            setError(
                resolveClientFormError(
                    err,
                    "onboarding",
                    "Legal entity details were not saved. Complete the required fields and try again."
                )
            );
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 4 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.2, ease: easings.outCubic }}
        >
            <Heading
                title="Legal Entity"
                className="pb-4 border-b border-line-base"
            />

            {showsDetails && (
                <FormCard
                    title="Legal Name"
                    hint="The entity's full legal name as it appears on the Formation Document."
                >
                    <Input
                        aria-label="Legal Name"
                        value={legalName}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                legalName: e.target.value
                            }))
                        }
                        placeholder="e.g. Crescent Wealth Holdings LLC"
                    />
                </FormCard>
            )}

            {showsDetails && (
                <FormCard
                    title="Trading Name (Optional)"
                    hint="If you want a different name displayed in the app."
                >
                    <Input
                        aria-label="Trading Name (Optional)"
                        value={tradingName}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                tradingName: e.target.value
                            }))
                        }
                        placeholder="e.g. Crescent"
                    />
                </FormCard>
            )}

            {showsDetails && (
                <FormCard
                    title="Entity Type"
                    hint="The formal type shown in your formation records."
                >
                    <LegalEntityTypeSelect
                        value={entityType}
                        onChange={(next) =>
                            setState((prev) => ({
                                ...prev,
                                entityType: next
                            }))
                        }
                    />
                </FormCard>
            )}

            {showsDetails && (
                <FormCard
                    title="Jurisdiction of Incorporation"
                    hint="Where the entity was formed."
                >
                    <CountrySelect
                        value={jurisdiction}
                        onChange={(j) =>
                            setState((prev) => ({ ...prev, jurisdiction: j }))
                        }
                    />
                </FormCard>
            )}

            {showsDetails && (
                <FormCard
                    title="Registered Address"
                    hint="The registered address in your formation document."
                >
                    <Input
                        aria-label="Registered Address"
                        value={registeredAddress}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                registeredAddress: e.target.value
                            }))
                        }
                        placeholder="e.g. 100 Main Street, London, SW1A 1AA, GB"
                    />
                </FormCard>
            )}

            {showsDetails && (
                <FormCard
                    title="Entity Number"
                    hint="The registration number in your formation document."
                >
                    <Input
                        aria-label="Entity Number"
                        value={companyNumber}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                companyNumber: e.target.value
                            }))
                        }
                        placeholder="e.g. CO-123456"
                    />
                </FormCard>
            )}

            {showsContacts && (
                <FormCard
                    title="Primary Contact Name"
                    hint="The main day-to-day point of contact for the entity."
                >
                    <Input
                        aria-label="Primary Contact Name"
                        value={primaryContactName}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                primaryContactName: e.target.value
                            }))
                        }
                        placeholder="e.g. Sarah Chen"
                    />
                </FormCard>
            )}

            {showsContacts && (
                <FormCard title="Primary Contact Email">
                    <Input
                        aria-label="Primary Contact Email"
                        type="email"
                        value={primaryContactEmail}
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                primaryContactEmail: e.target.value
                            }))
                        }
                        placeholder="e.g. ops@crescent.com"
                    />
                </FormCard>
            )}

            {showsContacts && (
                <FormCard
                    title="Legal Contact"
                    hint="Where legal notices will be served."
                >
                    {/* Names what the dropdown does — the placeholder
                        states the null case ("different than existing
                        contacts"), so the affirmative case needs saying
                        too. Not a <label>: FormCard already titles the
                        field, and the trigger carries its own aria-label. */}
                    <Text
                        variant="small"
                        className="block text-foreground-muted pb-2"
                    >
                        Same as Existing Contact?
                    </Text>
                    <Select
                        value={legalContactSource ?? ""}
                        onValueChange={(next) => {
                            setState((prev) => ({
                                ...prev,
                                legalContactSource:
                                    typeof next === "string" && next
                                        ? (next as LegalContactSource)
                                        : null
                            }));
                        }}
                    >
                        <SelectTrigger
                            aria-label="Legal contact source"
                            interaction="lightbar"
                            className="w-full min-w-0"
                        >
                            <SelectValue>
                                {
                                    LEGAL_CONTACT_OPTIONS.find(
                                        (o) =>
                                            o.value ===
                                            (legalContactSource ?? "")
                                    )?.label
                                }
                            </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                            {LEGAL_CONTACT_OPTIONS.map((opt) => (
                                <SelectItem key={opt.value} value={opt.value}>
                                    {opt.label}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </FormCard>
            )}

            {showsContacts && (
                <FormCard title="Legal Contact Name">
                    <Input
                        aria-label="Legal Contact Name"
                        disabled={legalContactSource !== null}
                        value={
                            legalContactSource === "primary"
                                ? primaryContactName
                                : legalContactSource === "signatory"
                                  ? signatory.signatoryName
                                  : legalContactName
                        }
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                legalContactName: e.target.value
                            }))
                        }
                        placeholder="e.g. Sarah Chen, General Counsel"
                    />
                </FormCard>
            )}

            {showsContacts && (
                <FormCard title="Legal Contact Email">
                    <Input
                        aria-label="Legal Contact Email"
                        type="email"
                        disabled={legalContactSource !== null}
                        value={
                            legalContactSource === "primary"
                                ? primaryContactEmail
                                : legalContactSource === "signatory"
                                  ? signatory.signatoryEmail
                                  : legalContactEmail
                        }
                        onChange={(e) =>
                            setState((prev) => ({
                                ...prev,
                                legalContactEmail: e.target.value
                            }))
                        }
                        placeholder="e.g. legal@crescent.com"
                    />
                </FormCard>
            )}

            {DOC_SLOTS.map((slot, docIdx) => {
                // Each document IS a sub-row (2.3 / 2.4 / 2.5).
                if (slotIdx !== undefined && slotIdx !== docIdx + 2)
                    return null;
                const purpose = slot.purpose as keyof LegalEntityDocsState;
                const current = docs[purpose];
                return (
                    <FormCard
                        key={slot.purpose}
                        title={slot.label}
                        hint={
                            slot.purpose === "certificate_of_good_standing"
                                ? "Must be issued within the last 90 days."
                                : undefined
                        }
                    >
                        <UploadFiles
                            className="max-w-sm mx-auto mt-6"
                            size="sm"
                            accept={DOCUMENT_ACCEPT}
                            maxSize={DOCUMENT_MAX_SIZE}
                            state={
                                uploadingPurpose === slot.purpose
                                    ? "uploading"
                                    : current
                                      ? "success"
                                      : "empty"
                            }
                            filename={current?.filename}
                            onSelect={(files) =>
                                void handleUpload(slot.purpose, files)
                            }
                        />
                    </FormCard>
                );
            })}

            <FormPrimaryAction
                submittable={submittable}
                submitting={submitting}
                isSavedClean={lastSubmitted !== null && !isDirty}
                nextStep={nextStep ?? null}
                onContinue={onContinue}
                onSave={() => void handleSave()}
                error={error}
                incompleteHint={incompleteHint}
            />
        </motion.div>
    );
}
